import { AxiosError } from 'axios';
import i18n from 'i18n';

const AUTHENTICATION_ERROR = /Please authenticate/i;

type NonFieldSpecificServerError = {
  keyword: string;
  message: string;
};

type FieldSpecificServerError =
  | {
      keyword: 'required';
      message: string;
      params: {
        missingProperty: string;
      };
    }
  | {
      instancePath: string;
      keyword: 'enum';
      params: {
        allowedValues: string[];
      };
    }
  | {
      instancePath: string;
      keyword: 'format';
      params: {
        format: string;
      };
    };

type ValidationErrors = {
  nonFieldErrors: { message: string }[];
  fieldErrors: {
    [key: string]: string;
  };
};

const isFieldSpecificError = (
  error: FieldSpecificServerError | NonFieldSpecificServerError,
): error is FieldSpecificServerError => {
  // @TODO(shawk): are there other kinds of validation errors that are field-specific
  return ['required', 'enum', 'format'].includes(error.keyword);
};

const getFieldNameFromInstancePath = (instancePath: string): string => {
  return instancePath[0] === '/' ? instancePath.slice(1) : instancePath;
};

const getFieldError = (error: FieldSpecificServerError): { name: string; message: string } => {
  switch (error.keyword) {
    case 'required':
      return {
        name: error.params.missingProperty,
        message: i18n.t('form.validation.fieldIsRequired'),
      };

    case 'enum':
      return {
        name: getFieldNameFromInstancePath(error.instancePath),
        message: i18n.t('form.validation.enumIsInvalid', { values: error.params.allowedValues.join(', ') }),
      };

    case 'format':
      return {
        name: getFieldNameFromInstancePath(error.instancePath),
        message: i18n.t('form.validation.formatIsInvalid', { format: error.params.format }),
      };

    default:
      throw new Error('Failed to parse field-specific error');
  }
};

const transformApiErrors = (
  errors: (FieldSpecificServerError | NonFieldSpecificServerError)[],
): ValidationErrors | undefined => {
  if (!errors.length) {
    return undefined;
  }

  const result: ValidationErrors = {
    nonFieldErrors: [],
    fieldErrors: {},
  };

  errors.forEach(error => {
    if (!isFieldSpecificError(error)) {
      result.nonFieldErrors.push({ message: error.message || i18n.t('form.validation.unknownError') });
    } else {
      const { name, message } = getFieldError(error);
      result.fieldErrors[name] = message;
    }
  });

  return result;
};

export const extractFieldErrors = (error: ValidationError | null): { [key: string]: string } | undefined => {
  return error?.validationErrors?.fieldErrors;
};

export class ApiError extends Error {
  originalError: AxiosError | undefined;

  constructor(message: string, options: { cause?: AxiosError | undefined }) {
    super(message);
    this.originalError = options?.cause;
  }

  static wrap(error: AxiosError): Error {
    /**
     * @NOTE(shawk): amplify can throw a few errors in the case that it cannot
     * refresh an access token which all contain the text "Please authenticate".
     * When encountering one of these errors, we convert it to an
     * UnauthorizedError which the application knows how to handle (log the user
     * out).
     */
    if (AUTHENTICATION_ERROR.test(error.message)) {
      return new UnauthorizedError(error.message, { cause: error });
    }

    if (!error.response || error.response.status < 200) {
      return new NetworkError(error.message, { cause: error });
    }

    switch (error.response.status) {
      case 401:
        return new UnauthorizedError(error.message, { cause: error });

      case 403:
        return new ForbiddenError(error.message, { cause: error });

      case 404:
        return new NotFoundError(error.message, { cause: error });

      // @TODO(shawk): This should align with whatever the backend uses to signal
      // validation errors. As of this writing postman shows 400's but 422 is
      // a more idiomatic status code for successful requests that contain
      // invalid data.
      case 400:
      case 422:
        return new ValidationError(error.message, { cause: error });

      default:
        return new UnexpectedError(error.message, { cause: error });
    }
  }

  get validationErrors(): ValidationErrors | undefined {
    // @TODO proper types here or delete code entirely since we don't use server-side validations nor we have a defined pattern
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return transformApiErrors((this.originalError?.response?.data as any) || []);
  }
}

export class NetworkError extends ApiError {}
export class UnexpectedError extends ApiError {}
export class NotFoundError extends ApiError {}
export class UnauthorizedError extends ApiError {}
export class ForbiddenError extends ApiError {}
export class ValidationError extends ApiError {}

export class ContentfulDataError extends Error {
  message = 'Bad contentful data';

  constructor(message: string) {
    super();
    this.message = `${this.message}: ${message}`;
  }
}

export const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  return String(error);
};
